import { getCountryBoundaryBox } from '../config/countries/index';

interface CoordinateValidationResult {
  isValid: boolean;
  error?: string;
  formatted?: number;
}

/**
 * Validate latitude coordinate
 * Valid range: -90 to +90 degrees
 */
export function validateLatitude(value: string | number | undefined): CoordinateValidationResult {
  if (value === undefined || value === null || value === '') {
    return { isValid: true }; // Allow empty values (optional field)
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Latitude must be a valid number'
    };
  }

  if (numValue < -90 || numValue > 90) {
    return {
      isValid: false,
      error: 'Latitude must be between -90 and +90 degrees'
    };
  }

  // Round to 6 decimal places for precision
  const formatted = Math.round(numValue * 1000000) / 1000000;

  return {
    isValid: true,
    formatted
  };
}

/**
 * Validate latitude coordinate with country-specific boundary box
 */
function validateLatitudeWithBoundary(
  value: string | number | undefined,
  boundaryBox: { minLatitude: number; maxLatitude: number; minLongitude: number; maxLongitude: number } | null,
  countryCode?: string
): CoordinateValidationResult {
  if (value === undefined || value === null || value === '') {
    return { isValid: true }; // Allow empty values (optional field)
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Latitude must be a valid number'
    };
  }

  if (boundaryBox) {
    // Use country-specific boundary box
    if (numValue < boundaryBox.minLatitude || numValue > boundaryBox.maxLatitude) {
      return {
        isValid: false,
        error: `Latitude must be between ${boundaryBox.minLatitude} and ${boundaryBox.maxLatitude} for ${countryCode || 'this country'}.`
      };
    }
  } else {
    // Fallback to global bounds
    if (numValue < -90 || numValue > 90) {
      return {
        isValid: false,
        error: 'Latitude must be between -90 and +90 degrees'
      };
    }
  }

  // Round to 6 decimal places for precision
  const formatted = Math.round(numValue * 1000000) / 1000000;

  return {
    isValid: true,
    formatted
  };
}

/**
 * Validate longitude coordinate
 * Valid range: -180 to +180 degrees
 */
export function validateLongitude(value: string | number | undefined): CoordinateValidationResult {
  if (value === undefined || value === null || value === '') {
    return { isValid: true }; // Allow empty values (optional field)
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Longitude must be a valid number'
    };
  }

  if (numValue < -180 || numValue > 180) {
    return {
      isValid: false,
      error: 'Longitude must be between -180 and +180 degrees'
    };
  }

  // Round to 6 decimal places for precision
  const formatted = Math.round(numValue * 1000000) / 1000000;

  return {
    isValid: true,
    formatted
  };
}

/**
 * Validate longitude coordinate with country-specific boundary box
 */
function validateLongitudeWithBoundary(
  value: string | number | undefined,
  boundaryBox: { minLatitude: number; maxLatitude: number; minLongitude: number; maxLongitude: number } | null,
  countryCode?: string
): CoordinateValidationResult {
  if (value === undefined || value === null || value === '') {
    return { isValid: true }; // Allow empty values (optional field)
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Longitude must be a valid number'
    };
  }

  if (boundaryBox) {
    // Use country-specific boundary box
    if (numValue < boundaryBox.minLongitude || numValue > boundaryBox.maxLongitude) {
      return {
        isValid: false,
        error: `Longitude must be between ${boundaryBox.minLongitude} and ${boundaryBox.maxLongitude} for ${countryCode || 'this country'}.`
      };
    }
  } else {
    // Fallback to global bounds
    if (numValue < -180 || numValue > 180) {
      return {
        isValid: false,
        error: 'Longitude must be between -180 and +180 degrees'
      };
    }
  }

  // Round to 6 decimal places for precision
  const formatted = Math.round(numValue * 1000000) / 1000000;

  return {
    isValid: true,
    formatted
  };
}

/**
 * Validate both latitude and longitude coordinates with optional country boundary box
 */
export function validateCoordinates(
  latitude: string | number | undefined,
  longitude: string | number | undefined,
  countryCode?: string
): {
  latitude: CoordinateValidationResult;
  longitude: CoordinateValidationResult;
  isValid: boolean;
} {
  // Get country-specific boundary box if available
  const boundaryBox = countryCode ? getCountryBoundaryBox(countryCode) : null;

  const latResult = validateLatitudeWithBoundary(latitude, boundaryBox, countryCode);
  const lonResult = validateLongitudeWithBoundary(longitude, boundaryBox, countryCode);

  return {
    latitude: latResult,
    longitude: lonResult,
    isValid: latResult.isValid && lonResult.isValid
  };
}

/**
 * Format coordinate for display (6 decimal places)
 */
export function formatCoordinate(value: number | undefined): string {
  if (value === undefined || value === null) return '';
  return value.toFixed(6);
}

/**
 * Check if coordinates are both provided (for mandatory validation)
 */
export function areCoordinatesComplete(
  latitude: string | number | undefined,
  longitude: string | number | undefined
): boolean {
  const hasLat = latitude !== undefined && latitude !== null && latitude !== '';
  const hasLon = longitude !== undefined && longitude !== null && longitude !== '';
  
  // Both must be provided or both must be empty
  return (hasLat && hasLon) || (!hasLat && !hasLon);
}
